import React from "react";
import AsyncSelect from "react-select/async";
import { useLazyGetProductsQuery } from "../../masters/products/productsApi";

const toOption = (p) => ({
  value: p.productCode,
  label: `${p.productName}${p.unitLabel ? " - " + p.unitLabel : ""}`,
  product: p,
});

// Products are branch-local (Product_Code collides across branches, same as
// custId), so this always searches within one branch - mirrors
// EmployeePicker.jsx's AsyncSelect shape.
// `isClearable` defaults to false (unchanged behavior for existing callers -
// a line-item row's product is required, not something to blank back out) -
// pass true explicitly for a picker used as a plain filter (e.g. Stock
// Movement Report).
// `groupId` (Batch185) narrows the search to one product group - an LPO row seeded
// from a quotation line that names a group, where the buyer picks the one model
// actually being ordered. The group's products are listed on open, without typing:
// the list is already short, and "what is in this group" is the question being asked.
const ProductPicker = ({ branchId, value, onChange, disabled, isClearable = false, groupId }) => {
  const [triggerSearch] = useLazyGetProductsQuery();

  const loadOptions = async (inputValue) => {
    const search = (inputValue || "").trim();
    if (!branchId || (!search && !groupId)) return [];
    const { data = [] } = await triggerSearch({ branchId, search: search || undefined, groupId: groupId || undefined });
    // A real group can hold hundreds of products (ALARMS in Mombasa: 865). Opened without
    // typing, show the first 50 - typing searches the whole group, so nothing is unreachable.
    return (search ? data : data.slice(0, 50)).map(toOption);
  };

  return (
    <AsyncSelect
      // Remount when the group changes - cacheOptions would otherwise serve the
      // previous group's results for the same typed text.
      key={groupId ? `g-${groupId}` : "all"}
      defaultOptions={Boolean(groupId)}
      classNamePrefix="react-select"
      cacheOptions
      isClearable={isClearable}
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      isDisabled={disabled || !branchId}
      placeholder={!branchId ? "Select a branch first" : groupId ? "Pick a product from the quoted group" : "Search product by name or code"}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No matching products" : "Type to search products"
      }
      // Renders the open menu through a portal on <body> instead of inline
      // in the line-items table cell - otherwise the table's own overflow/
      // stacking context clips the dropdown so results below the fold are
      // invisible/unclickable (reported: "product search... overflow or is
      // not seen outside the box").
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  );
};

export default ProductPicker;
